import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";

import { useGetCountries, useGetCountryCurrency } from "../../../api/static-data";
import { CommonModal, DropDown, InputText, NewImageUploader, TitleView } from "../../../components";
import useWindowDimensions from "../../../hooks/windowsSize";

import { trim } from "lodash";
import { usePostRegisterCompanyData, useUploadCompanyImage } from "../../../api/company";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import { InputDatePicker } from "../../../components/InputDatePicker/InputDatePicker";
import { InputNumber } from "../../../components/InputText/InputNumber";
import MainBottomButtonView from "../../../components/mainBottomButtonView/MainBottomButtonView";
import { THEME } from "../../../config/constants";
import { useCompanyContext } from "../../../context/CompanyContext";
import { successMessage } from "../../../helpers/ErrorHandler";
import "./companyRegister.scss";

const defaultCurrencyEuroId = 78;

const CompanyRegisterModal = ({ visibleModal, setVisibleModal, id }: any) => {
  const { height } = useWindowDimensions();
  const [visibleRegModal] = useState(false);
  const { triggerRefreshCompanyList } = useCompanyContext();
  const { isLoading, isSuccess, mutate } = usePostRegisterCompanyData();
  const { isLoading: isLoadingCompanyImage, mutate: uploadCompanyImage } = useUploadCompanyImage();
  const [country, setCountry] = useState<any>();
  const [startDate, setStartDate] = useState(new Date());
  const [currency, setCurrency] = useState<any>(defaultCurrencyEuroId);
  const [imageData, setImageData] = useState<File | null>(null);

  //company register status check
  useEffect(() => {
    if (isSuccess) {
      // Image upload is handled in onSubmit success callback
    }
  }, [isSuccess]);

  //custom hook rest data
  useEffect(() => {
    return () => {
      setCountry(null);
      setImageData(null);
      reset();
    };
  }, [visibleModal, visibleRegModal]);

  //handle company input data
  const values = useMemo(() => {
    return {
      name: "",
      address: "",
      companyEmail: "",
      noOfEmployees: 0,
      phone: "",
      tenantType: "Tenant",
      tenantCountry: 0,
      zipCode: "",
      noOfProducts: 0,
      industry: ""
    };
  }, [visibleModal, visibleRegModal]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    getValues
  } = useForm({
    defaultValues: values,
    values: values
  });

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const { data: countryCurrencyData, isLoading: isCountryCurrencyDataLoading } =
    useGetCountryCurrency();

  //company data submit
  const onSubmit = async (data: any) => {
    const submitData = {
      ...data,
      name: trim(data.name),
      status: "ACTIVE",
      isParent: false,
      tenantCountry: country,
      tenantType: "Tenant",
      dateOfSubscription: startDate,
      theme: THEME,
      currency: currency
    };
    mutate(submitData, {
      onSuccess: (response: any) => {
        const companyId = response?.data?._id;

        if (imageData && companyId) {
          // Upload image after company creation
          uploadCompanyImage(
            { company_id: companyId, file: imageData, customFileName: companyId },
            {
              onSuccess: () => {
                successMessage("Company and image registered successfully");
                setVisibleModal(false);
                triggerRefreshCompanyList();
              },
              onError: () => {
                successMessage("Company registered successfully (image upload failed)");
                setVisibleModal(false);
                triggerRefreshCompanyList();
              }
            }
          );
        } else {
          successMessage("Company registered successfully");
          setVisibleModal(false);
          triggerRefreshCompanyList();
        }
      }
    });
  };

  const countries = useMemo(() => {
    if (isCountriesDataSuccess) {
      return countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id
      }));
    }
  }, [countriesData, isCountriesDataSuccess]);

  const countryCurrency = useMemo(() => {
    return countryCurrencyData?.map((c: any) => ({
      key: c._id,
      text: `${c.country} - ${c.currency} - ${c.alphabeticCode}`,
      value: c._id
    }));
  }, [countryCurrencyData]);

  //handle loading
  if (isLoading || isLoadingCompanyImage) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => {
          setVisibleModal(false);
          setCountry(null);
          reset();
        }}
        centered={true}
        title="Company Register"
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16}>
            <>
              <form onSubmit={handleSubmit(onSubmit)}>
                <CommonSplitContainer
                  topChild={
                    <Grid>
                      <Grid.Column computer={16} tablet={16} mobile={16}>
                        <TitleView
                          title="Company Details"
                          CustomTitleViewMain="productRegisterTitle pl-0"
                        />
                        <Grid>
                          <Grid.Column computer={8} tablet={16} mobile={16} className="pb-0">
                            <InputText
                              register={register}
                              errors={errors.name}
                              labelName={"Company Name*"}
                              placeholder="Company Name"
                              name="name"
                              required={true}
                              errorMessage={"Please enter company name"}
                            />
                          </Grid.Column>
                          <Grid.Column computer={8} tablet={16} mobile={16} className="pb-0">
                            <InputText
                              register={register}
                              errors={errors.address}
                              labelName={"Address*"}
                              placeholder="Address"
                              name="address"
                              required={true}
                              errorMessage={"Please enter address"}
                            />
                          </Grid.Column>

                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.companyEmail}
                              labelName={"Company Email*"}
                              placeholder="Company Email"
                              name="companyEmail"
                              required={true}
                              errorMessage={"Please enter company email"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.phone}
                              labelName={"Phone"}
                              placeholder="phone"
                              name="phone"
                              required={false}
                              errorMessage={"Please enter name"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.phone}
                              labelName={"Registration Number"}
                              placeholder="Registration Number"
                              name="registrationNumber"
                              required={false}
                              errorMessage={"Please enter name"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <DropDown
                              labelName="Currency"
                              placeholder="Select currency"
                              defaultValue={currency}
                              currentData={countryCurrency}
                              handleChangeState={(e: any, { value }: any) => {
                                setCurrency(value);
                              }}
                              loading={isCountryCurrencyDataLoading}
                              search
                              key={"tenantCurrency"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.phone}
                              labelName={"Website"}
                              placeholder="Website"
                              name="website"
                              required={false}
                              errorMessage={"Please enter name"}
                            />
                          </Grid.Column>

                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputNumber
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              errors={errors.noOfEmployees}
                              name="noOfEmployees"
                              labelName={"No Of Employees"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputNumber
                              defaultValue={1}
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              errors={errors.noOfEmployees}
                              name="noOfUsers"
                              labelName={"No Of Users"}
                              removeDecimal={true}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputNumber
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              errors={errors.noOfProducts}
                              name="noOfProducts"
                              labelName={"No Of Products"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputNumber
                              defaultValue={1}
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              errors={errors.noOfProducts}
                              name="noOfSKUS"
                              labelName={"No Of SKUS"}
                              removeDecimal={true}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.phone}
                              labelName={"Industry"}
                              placeholder="Industry"
                              name="industry"
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <DropDown
                              labelName="Country"
                              placeholder="Select country"
                              defaultValue={country}
                              currentData={countries}
                              handleChangeState={(e: any, { value }: any) => {
                                setCountry(value);
                              }}
                              loading={isCountriesDataLoading}
                              search
                              key={"tenantCountry"}
                            />
                          </Grid.Column>

                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.zipCode}
                              labelName={"Zip Code"}
                              placeholder="zipCode"
                              name="zipCode"
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <InputDatePicker
                              labelName="Date of Subscription"
                              changeDate={setStartDate}
                              currentSelectedData={startDate}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop pb-0"
                          >
                            <NewImageUploader
                              mainTitle="Company Logo"
                              buttonTitle="Upload Logo"
                              imageData={(file: File | null) => setImageData(file)}
                              deleteImage={() => setImageData(null)}
                              defaultImage={null}
                              deleteButtonView={true}
                              uploadButtonView={true}
                              convertToPng
                            />
                          </Grid.Column>
                        </Grid>
                      </Grid.Column>
                    </Grid>
                  }
                  bottomChild={
                    <MainBottomButtonView
                      cancelButtonStatus
                      saveButtonStatus
                      cancelButton={() => {
                        setVisibleModal(false);
                        reset();
                      }}
                      saveButton={handleSubmit(onSubmit)}
                    />
                  }
                />
              </form>
            </>
          </Grid.Column>
        </Grid>
      </CommonModal>
    </>
  );
};

export default CompanyRegisterModal;
